<template>
  <ModalStep
    id="no-mentor-found-modal-content"
    :header="{
      title: t('matching.noMentorFoundModal.title'),
    }"
  >
    <template #bodyContent>
      <div class="warning-icon-wrapper" v-if="numberOfAttemptsRef <= 3">
        <WarningIcon color="warning" />
      </div>
      <div data-test-id="matching-popup-error" class="danger-icon-wrapper" v-else>
        <CancelIcon color="danger" />
      </div>

      <div>
        <p style="text-align: center" v-html="firstParagraphRef"></p>
        <br />
        <p style="text-align: center" v-html="secondParagraphRef"></p>
      </div>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="submit"
          color="white"
          :text="t('ui.button.close')"
          @emit:click="closeModal"
        />
        <CustomButton
          usage="button"
          type="submit"
          data-test-id="matching-popup-restart-research"
          color="secondary"
          :text="t('matching.noMentorFoundModal.button.restartResearch')"
          icon-position="left"
          @emit:click="restartResearch"
        >
          <template #icon>
            <ChangeIcon color="primary" />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import useGroups from '@/features/groups/hooks/use-groups.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CancelIcon from '@/features/ui/components/icons/cancel-icon.vue'
import ChangeIcon from '@/features/ui/components/icons/change-icon.vue'
import WarningIcon from '@/features/ui/components/icons/warning-icon.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import type { MenteeMatchingModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { MENTOR_SEARCH_ATTEMPTS_KEY } from '@/utils/config/constants'

const { t } = useI18n()
const { groupsRef } = useGroups()

const numberOfAttemptsRef = ref(parseInt(window.localStorage.getItem(MENTOR_SEARCH_ATTEMPTS_KEY) || '1'))

const firstParagraphRef = computed(() => {
  switch (numberOfAttemptsRef.value) {
    case 1:
      return t('matching.noMentorFoundModal.body.part1.1')
    case 2:
      return t('matching.noMentorFoundModal.body.part1.2')
    case 3:
      // same case as before
      return t('matching.noMentorFoundModal.body.part1.2')
    default:
      // last case
      return t('matching.noMentorFoundModal.body.part1.3')
  }
})

const secondParagraphRef = computed(() => {
  switch (numberOfAttemptsRef.value) {
    case 1:
      return t('matching.noMentorFoundModal.body.part2.1')
    case 2:
      return t('matching.noMentorFoundModal.body.part2.2')
    case 3:
      // same case as before
      return t('matching.noMentorFoundModal.body.part2.2')
    default:
      // last case
      return t('matching.noMentorFoundModal.body.part2.3')
  }
})

function closeModal() {
  modalsStore.toggleModal('no-mentor-found', false)
}

function restartResearch() {
  modalsStore.toggleModal('no-mentor-found', false)

  // if the user has already some groups, we show the mentee-matching modal
  // otherwise, we show the first matching modal (called mentee-matching)
  if (groupsRef.value.length > 0) {
    modalsStore.toggleModal('new-matching', true)
  } else {
    modalsStore.toggleModal('mentee-matching', true)
  }

  modalsStore.setModalStep<MenteeMatchingModalType>('mentee-matching', 'fill-criteria')
}
</script>

<style lang="scss">
@import './no-mentor-found-content.scss';
</style>
